// ════════════════════════════════════════════════════════════
//  online/callouts.js — a trainer's CALLS, shown over the fight
// ════════════════════════════════════════════════════════════
//  LYRAEI PLAYS (2026-10-17). caP'n: "connect Lyraei Plays to Shatterproof
//  so she can start playing, participating, and anticipating hehe aka
//  plotting and scheming". She plays like an anime trainer: her side fights
//  on Auto and SHE calls the shots ("S'aolesh, Ascend!"). Each call shows
//  for a few seconds under her name on every screen watching the fight.
//
//  ⚑ COSMETIC ONLY. A call is words, never a command: the commands that go
//    with it travel the lockstep door (sim/issue.js) like anyone's. So a
//    call can arrive late, twice or never and the FIGHT is unchanged.
//  ⚑ Offline (practice) the call stays on this screen. Online it is written
//    to  games/shatterproof/matches/{id}/calls/{side} = { x: text, t: server ms }
//    (the open games/ drawer — no rules change), by that side's own screen.
//    Testing-phase trust like the rest of the drawer (PRIME §2b).
// ════════════════════════════════════════════════════════════
import { useEffect, useState } from "react";
import { fbNet as net } from "./net.js";
import { serverNow } from "./ie.js";

export const CALL = {
  max: 60,          // ⟵ DIAL longest call (characters)
  show: 4.5,        // ⟵ DIAL seconds a call stays up
  gap: 2.5,         // ⟵ DIAL seconds between one side's calls (a brain stuck in a loop can't flood the screen)
};

export const cleanCall = (s) => String(s || "").replace(/\p{Cc}/gu, " ").replace(/[<>]/g, "").replace(/\s+/g, " ").trim().slice(0, CALL.max);

// the latest call per side, for the battle on screen
let latest = [null, null];
let forBattle = null;
const subs = new Set();
const ping = () => subs.forEach((f) => { try { f(latest); } catch (e) { /* one bad reader never stops the rest */ } });
const bind = (b) => { if (b !== forBattle) { forBattle = b; latest = [null, null]; } };
const lastSent = [0, 0];

/** Make a call for `side` of battle `b`. Returns { ok } or { ok:false, reason }. */
export function shout(b, side, text) {
  const x = cleanCall(text);
  if (!b || (side !== 0 && side !== 1)) return { ok: false, reason: "no fight on screen" };
  if (!x) return { ok: false, reason: "nothing to call" };
  const now = Date.now();
  if (now - lastSent[side] < CALL.gap * 1000) return { ok: false, reason: "slow down — one call at a time" };
  lastSent[side] = now;
  bind(b);
  const t = serverNow();
  latest = latest.slice(); latest[side] = { x, t }; ping();
  if (b.driver && b.driver.path && b.driver.mySide === side) Promise.resolve(net.set(`${b.driver.path}/calls/${side}`, { x, t })).catch(() => {});
  return { ok: true };
}

/** The calls to draw: [side0, side1], each { x, t } or null. Online it also listens to the match. */
export function useCalls(battle) {
  const [v, setV] = useState(latest);
  const b = battle && battle.current;
  const path = b && b.driver ? b.driver.path : null;
  useEffect(() => {
    bind(battle && battle.current);
    setV(latest);
    subs.add(setV);
    let off = null;
    if (path) off = net.onValue(`${path}/calls`, (val) => {
      if (!val) return;
      let changed = false; const next = latest.slice();
      for (const s of [0, 1]) {
        const c = val[s];
        if (c && typeof c.x === "string" && Number.isFinite(c.t) && (!next[s] || c.t > next[s].t)) { next[s] = { x: cleanCall(c.x), t: c.t }; changed = true; }
      }
      if (changed) { latest = next; ping(); }
    });
    return () => { subs.delete(setV); if (typeof off === "function") off(); };
  }, [battle, path]);
  return v;
}
